'use client';

import React from 'react';

import { AppleLogoIcon } from '@depop/web-ui-kit/Icons/AppleLogoIcon';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { APPLE_REDIRECT_URI } from '@/modules/auth/constants';
import {
  type AppleSignInResponse,
  SSOButtonVariant,
} from '@/modules/auth/types';
import { useAppleAuth } from '@/modules/auth/useAppleAuth';
import { Stdv1Stat } from '@/modules/observability/constants';
import { getLogger } from '@/modules/observability/logging';
import { recordMetric } from '@/modules/observability/metrics';

type AppleButtonProps = {
  handleSubmitTracking: () => void;
  variant?: SSOButtonVariant;
  handleAppleIDResponse: (
    credentials: AppleSignInResponse,
    redirect_uri: string
  ) => void;
};

function AppleButton({
  handleSubmitTracking,
  variant = SSOButtonVariant.Standard,
  handleAppleIDResponse,
}: AppleButtonProps) {
  const t = useTranslations('common');
  const pathname = usePathname();
  const { initSuccess } = useAppleAuth();

  const isEyeCatchingSSOButtonVariant =
    variant === SSOButtonVariant.EyeCatching;
  const isIconSSOButtonVariant = variant === SSOButtonVariant.Icon;

  function handleClick() {
    handleSubmitTracking();

    const redirect_uri = `${window.location.origin}${
      APPLE_REDIRECT_URI[pathname as keyof typeof APPLE_REDIRECT_URI] || ''
    }`;

    window.AppleID.auth
      .signIn({
        redirectURI: redirect_uri,
      })
      .then((credentials) => handleAppleIDResponse(credentials, redirect_uri))
      .catch(
        ({
          error,
        }: {
          error: 'popup_closed_by_user' | 'user_trigger_new_signin_flow';
        }) => {
          if (Stdv1Stat[`apple.auth.${error}_error`]) {
            recordMetric({
              type: 'increment',
              stat: Stdv1Stat[`apple.auth.${error}_error`],
            });
          } else {
            getLogger().error('AppleID.auth.signIn failure', {
              message: error,
            });
          }
        }
      );
  }

  return (
    <div
      className={styles.wrapper}
      style={
        {
          '--borderRadius':
            isEyeCatchingSSOButtonVariant || isIconSSOButtonVariant
              ? '40px'
              : '4px',
          ...(isIconSSOButtonVariant && { '--minWidth': '40px' }),
        } as React.CSSProperties
      }
    >
      {initSuccess && (
        <button
          className={styles.appleButton}
          onClick={handleClick}
          style={{
            ...(isIconSSOButtonVariant &&
              ({
                '--justifyContent': 'center',
              } as React.CSSProperties)),
          }}
        >
          <span
            className={styles.appleIconWrapper}
            style={
              {
                ...(!isIconSSOButtonVariant && {
                  '--paddingLeft': '12px', // To match the GoogleButton
                }),
              } as React.CSSProperties
            }
          >
            <AppleLogoIcon color="white" size={19} />
          </span>
          {!isIconSSOButtonVariant && (
            <span className={styles.buttonText}>
              {t('ContinueWithApple', { apple: 'Apple' })}
            </span>
          )}
        </button>
      )}
    </div>
  );
}

export { AppleButton };
